import React, { useMemo, useState } from "react";
import Table from "rc-table";
import { isDesktop } from "utils";
import { SVG_ENUM } from "enums";
import { Button, EmptyState, PopupModal, SVG } from "shared";
import { useGetSuccessorsDocs } from "hooks/successors";
import { desktopColumns, mobileColumns } from "./utilsDocumentsList";
import { successorTypeMap } from "types/SuccessorTypes";
import moment from "moment";
import DocumentSwitch from "../DocumentSwitch/DocumentSwitch";
import "./DocumentsList.scss";

type Doc = {
  id: string;
  uuid?: string;
  groupUuid?: string;
  name?: string;
  typeLabel?: string;
  createdAt?: string;
  type?: string;
  description?: string;
  file?: {
    id: number;
    uuid: string;
    name: string;
    url: string;
    createdAt?: string;
  };
};

type OwnerDocs = {
  ownerName: string;
  documents: Doc[];
};

const MobileDocDetails: React.FC<{ record: Doc }> = ({ record }) => {
  return (
    <div className="doc-details px-4 py-3">
      <div className="doc-details__row">
        <span>Typ:</span>
        <strong>{record.type && successorTypeMap[record.type]}</strong>
      </div>
      <div className="doc-details__row">
        <span>Data dodania:</span>
        <strong>{moment(record.createdAt).format("DD.MM.YYYY")}</strong>
      </div>
      {record.description ? (
        <div className="doc-details__row doc-details__desc">
          <span>Opis:</span>
          <p>{record.description}</p>
        </div>
      ) : null}
      <div className="doc-details__row">
        <span>Plik:</span>
        <strong>{record.file ? record.file.name : "Brak"}</strong>
      </div>
    </div>
  );
};

interface MyCompaniesTabType {
  setAddDocs: () => void;
  setDeleteDocument: (document: Doc) => void;
}

const DocumentsList: React.FC<MyCompaniesTabType> = ({
  setAddDocs,
  setDeleteDocument,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [playingVideo, setPlayingVideo] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const {
    data: { docs = [], canViewFiles = false, successorsData = [] } = {
      docs: [],
      canViewFiles: false,
      successorsData: [],
    },
  } = useGetSuccessorsDocs({ enabled: true });
  const notEmpty = !!(docs && docs.length > 0);
  const desktop = isDesktop();
  const rowKeyFn = (r: Doc) => r.groupUuid ?? r.uuid ?? r.id;

  const ownColumns = useMemo(
    () =>
      desktop
        ? desktopColumns({
            setShowDelete: (document: Doc) => setDeleteDocument(document),
            setPlayVideo: setPlayingVideo,
            canDelete: true,
          })
        : mobileColumns({
            setShowDelete: (document: Doc) => setDeleteDocument(document),
            setPlayVideo: setPlayingVideo,
            canDelete: true,
          }),
    [desktop, setDeleteDocument],
  );

  const ownerColumns = useMemo(
    () =>
      desktop
        ? desktopColumns({
            setPlayVideo: setPlayingVideo,
            canDelete: false,
          })
        : mobileColumns({
            setPlayVideo: setPlayingVideo,
            canDelete: false,
          }),
    [desktop],
  );

  const [ownersExpandedRows, setOwnersExpandedRows] = useState<
    Record<number, React.Key[]>
  >({});

  return (
    <div className="documents-list">
      <Button
        className="button--rounded button--black"
        size="medium"
        type="button"
        label="Dodaj dokument/notatkę"
        onClick={() => setAddDocs()}
      />
      {notEmpty ? (
        <>
          <DocumentSwitch canViewFiles={canViewFiles} />
          <div className="documents-list__header">Dodane dokumenty</div>
          <Table
            rowKey={rowKeyFn}
            className="companies-table documents-table"
            data={docs}
            columns={ownColumns}
            onRow={(record: Doc) => {
              const id = rowKeyFn(record);
              const isActive = expandedRowKeys.includes(id);
              return { className: isActive ? "active-row" : "" };
            }}
            expandable={
              desktop
                ? undefined
                : {
                    expandedRowRender: (record: Doc) => (
                      <MobileDocDetails record={record} />
                    ),
                    rowExpandable: () => true,
                    expandRowByClick: true,
                    showExpandColumn: false,
                    expandedRowKeys,
                    onExpand: (expanded, record: Doc) => {
                      const id = rowKeyFn(record);
                      setExpandedRowKeys((prev) =>
                        expanded ? [id] : prev.filter((k) => k !== id),
                      );
                    },
                  }
            }
          />
        </>
      ) : (
        <EmptyState
          className="successor-page__empty-state"
          svg={SVG_ENUM.INFO}
          text={
            <p>
              W tym miejscu pojawi się lista dodanych dokumentów. Aby dodać
              dokument należy kliknąć przycisk <strong>„Dodaj dokument”</strong>{" "}
              w prawym górnym rogu.
            </p>
          }
        />
      )}
      {successorsData.map((owner: OwnerDocs, idx: number) => {
        const ownerDocs = owner.documents || [];
        const ownerHasDocs = ownerDocs.length > 0;
        const ownerRowKeyFn = (r: Doc) =>
          r.groupUuid ?? r.uuid ?? r.id + "_owner_" + idx;
        const ownerExpanded = ownersExpandedRows[idx] || [];
        return (
          <div
            key={`owner-block-${idx}`}
            className="documents-list__owner-block mt-6"
          >
            <div className="documents-list__header">
              Dokumenty użytkownika {owner.ownerName}
            </div>
            {ownerHasDocs ? (
              <Table
                rowKey={ownerRowKeyFn}
                className="companies-table documents-table owner-documents-table"
                data={ownerDocs}
                columns={ownerColumns}
                onRow={(record: Doc) => {
                  const id = ownerRowKeyFn(record);
                  const isActive = ownerExpanded.includes(id);
                  return { className: isActive ? "active-row" : "" };
                }}
                expandable={
                  desktop
                    ? undefined
                    : {
                        expandedRowRender: (record: Doc) => (
                          <MobileDocDetails record={record} />
                        ),
                        rowExpandable: () => true,
                        expandRowByClick: true,
                        showExpandColumn: false,
                        expandedRowKeys: ownerExpanded,
                        onExpand: (expanded, record: Doc) => {
                          const id = ownerRowKeyFn(record);
                          setOwnersExpandedRows((prev) => {
                            const current = prev[idx] || [];
                            return {
                              ...prev,
                              [idx]: expanded
                                ? [id]
                                : current.filter((k) => k !== id),
                            };
                          });
                        },
                      }
                }
              />
            ) : (
              <EmptyState
                className="successor-page__empty-state"
                svg={SVG_ENUM.INFO}
                text={
                  <p>
                    {owner.ownerName} nie udostępnił jeszcze żadnych dokumentów.
                  </p>
                }
              />
            )}
          </div>
        );
      })}
      <PopupModal
        classNameOverlay="popup-modal__video-overlay"
        className="popup-modal__video"
        isOpen={!!playingVideo}
        onClose={() => setPlayingVideo(null)}
      >
        {playingVideo && (
          <div className="video-player-modal">
            <button
              type="button"
              className="video-player-modal__close"
              onClick={() => setPlayingVideo(null)}
              aria-label="Zamknij odtwarzacz"
            >
              <SVG type={SVG_ENUM.CLOSE} />
            </button>
            <video
              src={playingVideo.url}
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="video-player-modal__video"
            />
          </div>
        )}
      </PopupModal>
    </div>
  );
};

export default DocumentsList;
